import React, { useState, useRef, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { closeAddAttachment } from 'reducers/DialogsReducer';
import Dialog from 'components/Dialog';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Alert } from '@mui/material';
import TextField from '@material-ui/core/TextField';
import { uploadCADAttachment } from 'reducers/FileReducer';
import { notify } from 'reducers/NotifierReducer';
import { handleError } from 'reducers/ErrorReducer';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';
import Tooltip from 'components/Tooltip';
import GetAppIcon from '@material-ui/icons/GetApp';
import AttachmentIcon from 'components/AttachmentIcon';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';

const useStyles = makeStyles((theme) => ({
  txtField: {
    minWidth: 600,
  },
  fileInput: {
    display: 'none',
  },
  dropZone: {
    background: theme.colors.grey1,
    height: 150,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
    marginBottom: 8,
    '& svg': {
      marginRight: 8,
    },
  },
  dropZoneOver: {
    background: theme.colors.grey1,
    height: 150,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
    border: `3px dashed ${theme.palette.primary.main}`,
    marginBottom: 8,
    '& svg': {
      marginRight: 8,
    },
  },
  fileInfo: {
    minHeight: 35,
    '& img': {
      maxHeight: 24,
      borderRadius: 3,
      marginRight: 8,
    },
  },
  errorText: {
    color: 'red',
    margin: '0px 8px 10px',
    fontSize: '14px',
  },
  attachButton: {
    color: theme.button.color,
    borderColor: theme.button.color,
  },
}));

function AddAttachment(props) {
  const classes = useStyles();
  const { id, type, title = 'Add Attachment' } = props.data;
  const [Description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const [draggedOver, setDraggedOver] = useState(false);
  const mountedRef = useRef(true);
  const attachmentSizeLimit = useSelector((state) => state.config.attachmentSizeLimit);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const [capturedPreviewUrl, setCapturedPreviewUrl] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line
  }, []);

  const close = () => {
    if (mountedRef.current) setFile(null);
    props.closeAddAttachment();
  };

  const handleAttachmentChange = (event) => {
    const file = event.target.files[0];
    setFile(file);
    const fileLimitExceed = file.size > attachmentSizeLimit * 1024 * 1024;
    if (fileLimitExceed) {
      setError(`This file cannot be uploaded. Maximum size allowed: ${attachmentSizeLimit} MB.`);
    } else {
      setError('');
    }
  };

  const save = async () => {
    props.showSpinner();
    try {
      const newAttachments = [
        {
          parentType: type,
          parentId: id,
          file,
          fileName: file?.name,
          descr: Description,
          codeAttachmentType: null,
        },
      ];
      await uploadCADAttachment(newAttachments);
      if (type === 'Event') {
        props.notifyDataUpdate({ type: 'event', data: id });
      }
      props.notifyDataUpdate({ type: 'file-upload', data: type });
      close();
    } catch (err) {
      props.handleError(err);
    }
    props.hideSpinner();
  };

  const renderActions = () => {
    return (
      <>
        <Button
          color="primary"
          variant="contained"
          autoFocus
          onClick={save}
          disabled={!file || !!error}>
          <SaveIcon /> Save
        </Button>
        <Button color="primary" onClick={close}>
          <CloseIcon /> Close
        </Button>
      </>
    );
  };

  const onDrop = (ev) => {
    const files = ev.dataTransfer.files;
    if (!files.length) return setDraggedOver(false);
    setFile(files[0]);
    if (file.size > attachmentSizeLimit * 1024 * 1024) {
      setError(`File size limit exceeded. Maximum allowed per file: ${attachmentSizeLimit} MB.`);
    } else {
      setError('');
    }
    ev.preventDefault();
  };

  const onDragOver = (ev) => {
    if (!draggedOver) setDraggedOver(true);
    ev.preventDefault();
  };

  const onDragLeave = (ev) => {
    if (draggedOver) setDraggedOver(false);
    ev.preventDefault();
  };

  const renderFileInfo = () => {
    const idx = file.name.lastIndexOf('.');
    const type = file.name.substr(idx);
    return (
      <>
        <AttachmentIcon type={type} />
        <span>{file.name}</span>
      </>
    );
  };

  const onDescriptionChange = (e) => {
    const text = e.target.value;
    if (text.length > 100) return;
    setDescription(e.target.value);
  };

  const renderCameraActions = () => {
    return (cameraError ? (
      <Button onClick={closeCamera}>Close</Button>
    ) : capturedPreviewUrl ? (
      <>
        <Button
          className={classes.attachButton}
          onClick={handleRetake}
          startIcon={<ReplayIcon fontSize="small" />}>
          Retake
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmPhoto}
          color="primary"
          startIcon={<CheckIcon fontSize="small" />}>
          Use Photo
        </Button>
      </>
    ) : (
      <Button
        variant="contained"
        color="primary"
        onClick={handleSnapPhoto}
        startIcon={<CameraAltIcon fontSize="small" />}>
        Capture
      </Button>
    ));
  };

  const handleRetake = () => {
    if (capturedPreviewUrl) URL.revokeObjectURL(capturedPreviewUrl);
    setCapturedPreviewUrl(null);
    setCapturedBlob(null);
    startCameraStream();
  };

  const handleConfirmPhoto = () => {
    if (!capturedBlob) return;
    const file = new File([capturedBlob], `photo-${Date.now()}.jpg`, {
      type: 'image/jpeg',
    });
    setFile(file);
    closeCamera();
  };

  const handleSnapPhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Freeze the live feed while the user reviews the still frame.
    stopStream();

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        setCapturedBlob(blob);
        setCapturedPreviewUrl(URL.createObjectURL(blob));
      },
      'image/jpeg',
      0.92
    );
  };

  const startCameraStream = async () => {
    setCameraError(null);
    setCameraOpen(true);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Camera capture isn't supported in this browser. Use Attach File instead.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: 'environment',
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        setCameraError(
          'Camera access was denied. Allow camera permission in your browser/device settings, or use Attach File.'
        );
      } else if (err?.name === 'NotFoundError') {
        setCameraError('No camera was found on this device. Use Attach File instead.');
      } else {
        setCameraError("Couldn't access the camera. Use Attach File instead.");
      }
    }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const closeCamera = () => {
    stopStream();
    setCameraOpen(false);
    setCameraError(null);
    if (capturedPreviewUrl) URL.revokeObjectURL(capturedPreviewUrl);
    setCapturedPreviewUrl(null);
    setCapturedBlob(null);
  };

  const inputId = `upload-file-${type}-${id}`;
  const className = draggedOver || Boolean(file) ? classes.dropZoneOver : classes.dropZone;
  return (
    <>
      <Dialog toolbar onClose={close} title={title} actions={renderActions()}>
        <div
          className={className}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}>
          <Box style={{ display: 'flex', gap: 8 }}>
            <Tooltip title="Add Attachment">
              <Button
                color="primary"
                variant="outlined"
                component="span"
                className={classes.attachButton}
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.click();
                  }
                  setFile(null);
                  setError('');
                }}>
                <GetAppIcon /> Add Attachment
              </Button>
            </Tooltip>
            <Tooltip title="Camera">
              <Button
                variant="outlined"
                component="span"
                className={classes.attachButton}
                onClick={startCameraStream}>
                <CameraAltIcon />
                Camera
              </Button>
            </Tooltip>
          </Box>
          <input
            accept="*"
            className={classes.fileInput}
            onChange={handleAttachmentChange}
            id={inputId}
            type="file"
            ref={fileInputRef}
          />
        </div>
        <div className={classes.fileInfo}>{Boolean(file) && renderFileInfo()}</div>
        {error && <div className={classes.errorText}>{error}</div>}
        <TextField
          label="Description"
          variant="outlined"
          size="small"
          value={Description}
          onChange={onDescriptionChange}
          className={classes.txtField}
        />
      </Dialog>

      <Dialog
        open={cameraOpen}
        onClose={closeCamera}
        title={capturedPreviewUrl ? 'Review Photo' : 'Take Photo'}
        maxWidth="sm"
        fullWidth
        actions={renderCameraActions()}>
        {cameraError ? (
          <Alert severity="error">{cameraError}</Alert>
        ) : (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: 'black',
              aspectRatio: '4 / 3',
            }}>
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: capturedPreviewUrl ? 'none' : 'block',
              }}
            />
            {capturedPreviewUrl && (
              <img
                src={capturedPreviewUrl}
                alt="Captured preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            )}
          </Box>
        )}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Dialog>
    </>
  );
}

export default connect(null, {
  closeAddAttachment,
  notify,
  handleError,
  showSpinner,
  hideSpinner,
  notifyDataUpdate,
})(AddAttachment);
